import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, IconButton,
  CircularProgress, Divider, Tooltip, Snackbar,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import RestoreIcon from '@material-ui/icons/Restore';
import axios from 'axios';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS, AREA_CATEGORIES,
  SERVICES, SERVICE_LABELS, DEFAULT_SERVICES_BY_TYPE,
} from '../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: 900,
    width: '90vw',
    maxHeight: '85vh',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  content: {
    padding: '16px 20px',
    overflowY: 'auto',
  },
  categoryHeader: {
    fontSize: 12,
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 20,
    marginBottom: 8,
  },
  typeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  typeLabel: {
    minWidth: 180,
    maxWidth: 180,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    paddingTop: 4,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  serviceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    flex: 1,
  },
  chip: {
    fontSize: 10,
    height: 24,
    cursor: 'pointer',
  },
  chipActive: {
    background: '#1565c0',
    color: '#fff',
    '&:hover': { background: '#1255a0' },
  },
  chipInactive: {
    background: '#f5f5f5',
    color: '#999',
    border: '1px solid #e0e0e0',
    '&:hover': { background: '#eee', color: '#666' },
  },
  resetBtn: {
    fontSize: 10,
    minWidth: 0,
    padding: '2px 6px',
    color: '#999',
    flexShrink: 0,
  },
  actions: {
    padding: '12px 20px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dirty: {
    fontSize: 11,
    color: '#ff9800',
    fontWeight: 500,
  },
}));

// All service codes in display order
const ALL_SERVICES = Object.values(SERVICES);

const cloneDefaults = (defaults = {}) =>
  Object.keys(AREA_TYPE_LABELS).reduce((acc, typeKey) => ({
    ...acc,
    [typeKey]: [...(defaults[typeKey] || [])],
  }), {});

const systemDefaults = () =>
  Object.keys(AREA_TYPE_LABELS).reduce((acc, typeKey) => ({
    ...acc,
    [typeKey]: [...(DEFAULT_SERVICES_BY_TYPE[typeKey] || [])],
  }), {});

// Group area types by category
function groupByCategory() {
  const groups = {};
  Object.entries(AREA_TYPE_LABELS).forEach(([typeKey, label]) => {
    const cat = AREA_CATEGORIES[typeKey] || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push({ key: typeKey, label });
  });
  return groups;
}

const AreaTypeDefaultsModal = ({ open, onClose, url }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [savedDefaults, setSavedDefaults] = useState({});
  const [dirty, setDirty] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const grouped = groupByCategory();

  // Load saved defaults
  useEffect(() => {
    if (!open || !url) return;
    setLoading(true);
    axios.get(`${url}/api/customer/area-type-defaults`)
      .then(resp => {
        const saved = resp.data?.defaults || {};
        // Merge with hardcoded: use saved if present, else hardcoded
        const merged = {};
        Object.keys(AREA_TYPE_LABELS).forEach(typeKey => {
          merged[typeKey] = saved[typeKey] || DEFAULT_SERVICES_BY_TYPE[typeKey] || [];
        });
        setDefaults(merged);
        setSavedDefaults(cloneDefaults(merged));
        setDirty(false);
      })
      .catch(() => {
        // Fall back to hardcoded
        const merged = systemDefaults();
        setDefaults(merged);
        setSavedDefaults(cloneDefaults(merged));
        setDirty(false);
      })
      .finally(() => setLoading(false));
  }, [open, url]);

  const toggleService = useCallback((typeKey, serviceCode) => {
    setDefaults(prev => {
      const current = prev[typeKey] || [];
      const next = current.includes(serviceCode)
        ? current.filter(s => s !== serviceCode)
        : [...current, serviceCode];
      return { ...prev, [typeKey]: next };
    });
    setDirty(true);
  }, []);

  const resetType = useCallback((typeKey) => {
    setDefaults(prev => ({
      ...prev,
      [typeKey]: [...(savedDefaults[typeKey] || [])],
    }));
    setDirty(true);
  }, [savedDefaults]);

  const resetAll = useCallback(() => {
    setDefaults(cloneDefaults(savedDefaults));
    setDirty(false);
    setSnackMsg('Restored saved defaults');
  }, [savedDefaults]);

  const useSystemDefaults = useCallback(() => {
    setDefaults(systemDefaults());
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!url) return;
    setSaving(true);
    try {
      await axios.put(`${url}/api/customer/area-type-defaults`, { defaults });
      const merged = cloneDefaults(defaults);
      setDefaults(merged);
      setSavedDefaults(merged);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('siteTakeoff:areaTypeDefaultsUpdated', {
          detail: { defaults: merged },
        }));
      }
      setDirty(false);
      setSnackMsg('Defaults saved');
    } catch (err) {
      console.error('Save error:', err);
      setSnackMsg('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        classes={{ paper: classes.dialogPaper }}
        maxWidth={false}
      >
        <div className={classes.title}>
          <Typography style={{ fontSize: 16, fontWeight: 500 }}>
            Default Services by Area Type
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Restore saved company defaults">
              <Button
                size="small"
                startIcon={<RestoreIcon style={{ fontSize: 14 }} />}
                onClick={resetAll}
                style={{ textTransform: 'none', fontSize: 12 }}
              >
                Reset all
              </Button>
            </Tooltip>
            <Tooltip title="Use hardcoded system defaults">
              <Button
                size="small"
                onClick={useSystemDefaults}
                style={{ textTransform: 'none', fontSize: 12 }}
              >
                System defaults
              </Button>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </div>

        <div className={classes.content}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            Object.entries(grouped).map(([category, types]) => (
              <Box key={category}>
                <Typography className={classes.categoryHeader}>{category}</Typography>
                {types.map(({ key, label }) => {
                  const services = defaults[key] || [];
                  return (
                    <div key={key} className={classes.typeRow}>
                      <div className={classes.typeLabel}>
                        <span
                          className={classes.colorDot}
                          style={{ background: AREA_TYPE_COLORS[key] || '#999' }}
                        />
                        {label}
                      </div>
                      <div className={classes.serviceChips}>
                        {ALL_SERVICES.map(svc => {
                          const active = services.includes(svc);
                          return (
                            <Chip
                              key={svc}
                              label={SERVICE_LABELS[svc] || svc}
                              size="small"
                              className={`${classes.chip} ${active ? classes.chipActive : classes.chipInactive}`}
                              onClick={() => toggleService(key, svc)}
                            />
                          );
                        })}
                      </div>
                      <Tooltip title="Reset to system default">
                        <Button
                          size="small"
                          className={classes.resetBtn}
                          onClick={() => resetType(key)}
                        >
                          Reset
                        </Button>
                      </Tooltip>
                    </div>
                  );
                })}
              </Box>
            ))
          )}
        </div>

        <div className={classes.actions}>
          <span className={classes.dirty}>
            {dirty ? 'Unsaved changes' : ''}
          </span>
          <Box display="flex" gap={1}>
            <Button onClick={onClose} size="small" style={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              size="small"
              variant="contained"
              color="primary"
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon style={{ fontSize: 14 }} />}
              style={{ textTransform: 'none' }}
            >
              {saving ? 'Saving...' : 'Save defaults'}
            </Button>
          </Box>
        </div>
      </Dialog>

      <Snackbar
        open={!!snackMsg}
        autoHideDuration={3000}
        onClose={() => setSnackMsg('')}
        message={snackMsg}
      />
    </>
  );
};

export default AreaTypeDefaultsModal;
